import React, {MutableRefObject, useEffect, useMemo, useRef, useState} from 'react';
import type {OpenSheetMusicDisplay} from 'opensheetmusicdisplay';
import {
    CircleCheck, Download, FileMusic, LoaderCircle, Music, Pause, Piano, Play, Printer, Square, Upload,
} from 'lucide-react';
import './Transposer.css';
import {
    analyzeScore, isBlackKey, keyName, KeyOption, keyOptions, Mode, nearestShift, parseScore,
    PdfNotSupportedError, rankOptions, readMusicFile, scalePitchClasses, ScoreAnalysis, semitoneLabel,
    serializeScore, signatureLabel, transposeScore,
} from './musicxml';
import {
    analyzeMidi, MidiAnalysis, MidiFile, midiPedals, midiTempos, midiTimeline, parseMidi, transposeMidi,
    transposeTimeline,
} from './midi';
import {midiToMusicXml} from './midiToXml';
import {polishImportedScore, titleFromFileName} from './polishImport';
import {EngineStatus, engrave, Engraving, importMidiToXml, loadEngine, subscribeEngineStatus} from './engine';
import {
    buildTimeline, formatTime, PianoStatus, preloadPiano, ScorePlayer, subscribePianoStatus, TempoMap, Timeline,
} from './player';

type Direction = 'up' | 'down';

// A MIDI file is also turned into a score, so both kinds can be shown as notes and printed
type Source =
    | { kind: 'xml'; doc: Document }
    | { kind: 'midi'; doc: Document; midi: MidiFile; timeline: Timeline; analysis: MidiAnalysis };

const PIANO_LOW = 21;
const PIANO_HIGH = 108;
const MANUAL_KEYS = [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7];

function percent(ratio: number) {
    return `${Math.round(ratio * 100)}%`;
}

function shiftFor(option: KeyOption, direction: Direction | null) {
    if (option.pcShift === 0) return 0;
    if (direction === 'up') return option.pcShift;
    if (direction === 'down') return option.pcShift - 12;
    return nearestShift(option.pcShift);
}

function saveFile(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}

export default function Transposer() {
    const [fileName, setFileName] = useState<string | null>(null);
    const [source, setSource] = useState<Source | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pdfDropped, setPdfDropped] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [manualFifths, setManualFifths] = useState(-6);
    const [manualMode, setManualMode] = useState<Mode>('major');
    const [target, setTarget] = useState<number | null>(null);
    const [direction, setDirection] = useState<Direction | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [showRoll, setShowRoll] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // Set by the engraved view: exports the score it shows as PDF
    const pdfRef = useRef<(() => Promise<Uint8Array>) | null>(null);
    const engine = useEngineStatus();

    useEffect(() => {
        preloadPiano();
        void loadEngine();
    }, []);

    const score = source?.doc ?? null;
    const midi = source?.kind === 'midi' ? source : null;
    const analysis = useMemo<ScoreAnalysis | MidiAnalysis | null>(() => {
        if (source?.kind === 'xml') return analyzeScore(source.doc);
        return source?.analysis ?? null;
    }, [source]);
    const fromFifths = analysis ? analysis.fifths : manualFifths;
    const mode = analysis ? analysis.mode : manualMode;
    const counts = useMemo(
        () => (analysis ? analysis.pitchClassCounts : scalePitchClasses(manualFifths, manualMode)),
        [analysis, manualFifths, manualMode]);
    const ranked = useMemo(() => rankOptions(keyOptions(fromFifths, counts)), [fromFifths, counts]);
    const original = ranked.find(o => o.pcShift === 0)!;
    const selected = ranked.find(o => o.fifths === target) ?? ranked[0];
    const semitones = shiftFor(selected, direction);

    const originalXml = useMemo(() => (score ? serializeScore(score) : null), [score]);
    const transposedXml = useMemo(
        () => (score ? serializeScore(transposeScore(score, selected.fifthsDelta, semitones)) : null),
        [score, selected.fifthsDelta, semitones]);
    const transposedRoll = useMemo(
        () => (midi ? transposeTimeline(midi.timeline, semitones) : null),
        [midi, semitones]);

    const fromName = keyName(fromFifths, mode);
    const toName = keyName(selected.fifths, mode);

    const outOfRange = analysis && analysis.noteCount > 0
        && (analysis.minMidi + semitones < PIANO_LOW || analysis.maxMidi + semitones > PIANO_HIGH);

    async function openFile(file: File) {
        setError(null);
        setPdfDropped(false);
        try {
            const music = await readMusicFile(file);
            if (music.kind === 'midi') {
                const parsed = parseMidi(music.bytes);
                const timeline = midiTimeline(parsed);
                const midiAnalysis = analyzeMidi(parsed, timeline);
                const title = titleFromFileName(file.name);
                let doc: Document;
                try {
                    // MuseScore's importer handles voices, tuplets and hands far better than the simple one
                    setBusy('Распознаю ноты из MIDI…');
                    doc = polishImportedScore(parseScore(await importMidiToXml(music.bytes)), {
                        title,
                        pedals: midiPedals(parsed),
                        tempos: midiTempos(parsed),
                    });
                } catch (e) {
                    console.error(e);
                    doc = midiToMusicXml(parsed, {fifths: midiAnalysis.fifths, mode: midiAnalysis.mode, title});
                }
                // Transposition is relative to the key written in the score
                const written = analyzeScore(doc);
                setSource({
                    kind: 'midi', doc, midi: parsed, timeline,
                    analysis: {...midiAnalysis, fifths: written.fifths, mode: written.mode},
                });
            } else {
                setSource({kind: 'xml', doc: parseScore(music.text)});
            }
            setFileName(file.name);
            setTarget(null);
            setDirection(null);
            setShowOriginal(false);
            setShowRoll(false);
        } catch (e) {
            if (e instanceof PdfNotSupportedError) {
                setPdfDropped(true);
            } else {
                setError(e instanceof Error ? e.message : String(e));
            }
        } finally {
            setBusy(null);
        }
    }

    function selectKey(fifths: number) {
        setTarget(fifths);
        setDirection(null);
        setShowOriginal(false);
    }

    const baseName = `${(fileName ?? 'score').replace(/\.(musicxml|xml|mxl|midi?)$/i, '')} (${toName.letter})`;

    function downloadXml() {
        if (transposedXml) {
            saveFile(new Blob([transposedXml], {type: 'application/vnd.recordare.musicxml+xml'}), `${baseName}.musicxml`);
        }
    }

    async function downloadPdf() {
        if (!pdfRef.current) {
            window.print();
            return;
        }
        setBusy('Готовлю PDF…');
        try {
            saveFile(new Blob([await pdfRef.current()], {type: 'application/pdf'}), `${baseName}.pdf`);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(null);
        }
    }

    function downloadMidi() {
        if (midi) {
            const bytes = transposeMidi(midi.midi, selected.fifthsDelta, semitones);
            saveFile(new Blob([bytes], {type: 'audio/midi'}), `${baseName}.mid`);
        }
    }

    return (
        <div className="TpPage">
            <header className="TpHeader TpNoPrint">
                <div>
                    <h1>Транспонирование нот</h1>
                    <p>Подбирает тональность, в которой ноты ложатся на белые клавиши, и переписывает партитуру.</p>
                </div>
                <div className="TpBadges">
                    <PianoBadge/>
                    <EngineBadge status={engine}/>
                </div>
            </header>

            <section className="TpCard TpNoPrint">
                <div className={`TpDrop${dragOver ? ' TpDrop--over' : ''}`}
                     onClick={() => inputRef.current?.click()}
                     onDragOver={e => {
                         e.preventDefault();
                         setDragOver(true);
                     }}
                     onDragLeave={() => setDragOver(false)}
                     onDrop={e => {
                         e.preventDefault();
                         setDragOver(false);
                         const file = e.dataTransfer.files[0];
                         if (file) openFile(file);
                     }}>
                    <Upload size={26}/>
                    <div>
                        <strong>{busy ?? fileName ?? 'Перетащите файл с нотами'}</strong>
                        <span>MusicXML (.musicxml, .xml, .mxl) или MIDI (.mid) — или нажмите, чтобы выбрать</span>
                    </div>
                    <input ref={inputRef} type="file" hidden accept=".musicxml,.xml,.mxl,.mid,.midi,.pdf"
                           onChange={e => {
                               const file = e.target.files?.[0];
                               if (file) openFile(file);
                               e.target.value = '';
                           }}/>
                </div>
                {error && <p className="TpError">{error}</p>}
                {pdfDropped && (
                    <div className="TpNotice">
                        <strong>PDF — это картинка нот, а не сами ноты.</strong>
                        <p>Сначала его нужно распознать в MusicXML, это делается один раз:</p>
                        <ol>
                            <li><a href="https://audiveris.github.io/audiveris/" target="_blank"
                                   rel="noreferrer">Audiveris</a> (бесплатно): открыть PDF → Book → Transcribe →
                                Export as MusicXML.
                            </li>
                            <li>Открыть результат в <a href="https://musescore.org" target="_blank"
                                                       rel="noreferrer">MuseScore</a> и поправить ошибки
                                распознавания, затем «Экспорт → MusicXML».
                            </li>
                            <li>Загрузить сюда .musicxml или .mxl.</li>
                        </ol>
                        <p>Пока можно выбрать тональность вручную ниже — таблица покажет, куда лучше сдвинуть.</p>
                    </div>
                )}
                {!source && (
                    <div className="TpManual">
                        <span>Нет файла? Тональность вручную:</span>
                        <select value={manualFifths} onChange={e => {
                            setManualFifths(Number(e.target.value));
                            setTarget(null);
                        }}>
                            {MANUAL_KEYS.map(f => (
                                <option key={f} value={f}>
                                    {keyName(f, manualMode).ru} ({signatureLabel(f)})
                                </option>
                            ))}
                        </select>
                        <div className="TpSegmented">
                            {(['major', 'minor'] as Mode[]).map(m => (
                                <button key={m} className={manualMode === m ? 'is-active' : ''}
                                        onClick={() => setManualMode(m)}>
                                    {m === 'major' ? 'мажор' : 'минор'}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <section className="TpCard TpNoPrint">
                <div className="TpSummary">
                    <div>
                        <span className="TpLabel">{analysis ? 'Сейчас' : 'Гамма'}</span>
                        <strong>{fromName.ru}</strong>
                        <span className="TpMuted">{fromName.letter} · {signatureLabel(fromFifths)}
                            · чёрных {percent(original.blackRatio)}</span>
                    </div>
                    <div className="TpArrow">→</div>
                    <div>
                        <span className="TpLabel">Будет</span>
                        <strong>{toName.ru}</strong>
                        <span className="TpMuted">{toName.letter} · {signatureLabel(selected.fifths)}
                            · чёрных {percent(selected.blackRatio)}</span>
                    </div>
                </div>
                {analysis && (
                    <p className="TpMuted">
                        {analysis.noteCount} нот в файле{analysis.noteCount === 0 && ' — нечего анализировать'}.
                        Доля чёрных клавиш считается по всем нотам, включая случайные знаки.
                        {'keyFromFile' in analysis && (analysis.keyFromFile
                            ? ' Тональность взята из MIDI-файла.'
                            : ' В MIDI нет тональности, она определена по нотам и может быть неточной — на подбор это не влияет.')}
                        {midi && ' Ноты собраны из MIDI автоматически: ритм выровнен по шестнадцатым, руки и голоса разведены по высоте и дорожкам.'}
                    </p>
                )}

                <div className="TpKeyboards">
                    <Keyboard title="Было" counts={counts} shift={0}/>
                    <Keyboard title="Стало" counts={counts} shift={selected.pcShift}/>
                </div>

                <table className="TpTable">
                    <thead>
                    <tr>
                        <th>Тональность</th>
                        <th>Знаки</th>
                        <th>Сдвиг</th>
                        <th>Чёрные клавиши</th>
                    </tr>
                    </thead>
                    <tbody>
                    {ranked.map((option, i) => {
                        const name = keyName(option.fifths, mode);
                        return (
                            <tr key={option.fifths}
                                className={option.fifths === selected.fifths ? 'is-selected' : ''}
                                onClick={() => selectKey(option.fifths)}>
                                <td>
                                    {name.ru} <span className="TpMuted">{name.letter}</span>
                                    {i === 0 && <span className="TpBadge">лучшая</span>}
                                    {option.pcShift === 0 && <span className="TpBadge TpBadge--plain">исходная</span>}
                                </td>
                                <td>{signatureLabel(option.fifths)}</td>
                                <td>{semitoneLabel(nearestShift(option.pcShift))}</td>
                                <td>
                                    <div className="TpBar">
                                        <div style={{width: percent(option.blackRatio)}}/>
                                    </div>
                                    {percent(option.blackRatio)}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </section>

            {source && (
                <section className="TpCard TpSheetCard">
                    <div className="TpToolbar TpNoPrint">
                        {selected.pcShift !== 0 && (
                            <div className="TpSegmented">
                                <button className={semitones > 0 ? 'is-active' : ''} onClick={() => setDirection('up')}>
                                    {semitoneLabel(selected.pcShift)}
                                </button>
                                <button className={semitones < 0 ? 'is-active' : ''}
                                        onClick={() => setDirection('down')}>
                                    {semitoneLabel(selected.pcShift - 12)}
                                </button>
                            </div>
                        )}
                        <div className="TpSegmented">
                            <button className={!showOriginal ? 'is-active' : ''}
                                    onClick={() => setShowOriginal(false)}>{toName.letter}</button>
                            <button className={showOriginal ? 'is-active' : ''}
                                    onClick={() => setShowOriginal(true)}>оригинал</button>
                        </div>
                        {midi && (
                            <div className="TpSegmented">
                                <button className={!showRoll ? 'is-active' : ''}
                                        onClick={() => setShowRoll(false)}>ноты</button>
                                <button className={showRoll ? 'is-active' : ''}
                                        onClick={() => setShowRoll(true)}>piano-roll</button>
                            </div>
                        )}
                        <div className="TpActions">
                            <button className="TpButton" onClick={downloadXml}><Download size={16}/> MusicXML</button>
                            {midi && <button className="TpButton" onClick={downloadMidi}><Download size={16}/> MIDI</button>}
                            <button className="TpButton TpButton--solid" onClick={downloadPdf}
                                    disabled={(!!midi && showRoll) || !!busy}>
                                <Printer size={16}/> PDF
                            </button>
                        </div>
                    </div>
                    {outOfRange && (
                        <p className="TpError TpNoPrint">
                            После сдвига часть нот выходит за диапазон фортепиано — попробуйте другое направление.
                        </p>
                    )}
                    {midi && transposedRoll && showRoll
                        ? <MidiView timeline={showOriginal ? midi.timeline : transposedRoll}/>
                        : originalXml && transposedXml && (engine.state === 'failed'
                        ? <SheetView xml={showOriginal ? originalXml : transposedXml}/>
                        : <EngravedSheet xml={showOriginal ? originalXml : transposedXml}
                                         sound={midi ? (showOriginal ? midi.timeline : transposedRoll) : null}
                                         pdfRef={pdfRef}/>)}
                </section>
            )}

            {!source && (
                <section className="TpCard TpEmpty TpNoPrint">
                    <FileMusic size={32}/>
                    <p>Загрузите MusicXML или MIDI, чтобы увидеть и послушать музыку в новой тональности.</p>
                </section>
            )}
        </div>
    );
}

function usePianoStatus(): PianoStatus {
    const [status, setStatus] = useState<PianoStatus>({state: 'idle', progress: 0});
    useEffect(() => subscribePianoStatus(setStatus), []);
    return status;
}

function useEngineStatus(): EngineStatus {
    const [status, setStatus] = useState<EngineStatus>({state: 'idle', progress: 0});
    useEffect(() => subscribeEngineStatus(setStatus), []);
    return status;
}

function StatusBadge({state, progress, icon, label}: {
    state: 'loading' | 'ready' | 'problem';
    progress: number;
    icon: React.ReactNode;
    label: string;
}) {
    return (
        <div className={`TpPianoBadge TpPianoBadge--${state}`} role="status" aria-live="polite">
            {state === 'ready' ? <CircleCheck size={18}/> : icon}
            <div className="TpPianoBadge-text">
                <span>{label}</span>
                {state === 'loading' && (
                    <div className="TpPianoBadge-bar" aria-hidden="true">
                        <div style={{width: `${Math.round(progress * 100)}%`}}/>
                    </div>
                )}
            </div>
            {state === 'loading' && <span className="TpPianoBadge-pct">{Math.round(progress * 100)}%</span>}
        </div>
    );
}

function PianoBadge() {
    const {state, progress} = usePianoStatus();
    if (state === 'idle') return null;
    return (
        <StatusBadge state={state === 'fallback' ? 'problem' : state} progress={progress} icon={<Piano size={18}/>}
                     label={state === 'loading' ? 'Загружаю звук рояля'
                         : state === 'ready' ? 'Рояль готов' : 'Рояль не загрузился — играет простой звук'}/>
    );
}

function EngineBadge({status: {state, progress}}: { status: EngineStatus }) {
    if (state === 'idle') return null;
    return (
        <StatusBadge state={state === 'failed' ? 'problem' : state} progress={progress} icon={<Music size={18}/>}
                     label={state === 'loading' ? 'Загружаю нотный движок'
                         : state === 'ready' ? 'Нотный движок готов' : 'Нотный движок не загрузился — упрощённые ноты'}/>
    );
}

const WHITE_PCS = [0, 2, 4, 5, 7, 9, 11];
const BLACK_KEYS: Array<[number, number]> = [[1, 1], [3, 2], [6, 4], [8, 5], [10, 6]];

// One octave; each key is tinted by how often the piece lands on it
function Keyboard({title, counts, shift}: { title: string; counts: number[]; shift: number }) {
    const usage = Array.from({length: 12}, (_, pc) => counts[(pc - shift + 12) % 12]);
    const max = Math.max(1, ...usage);
    const black = usage.filter((_, pc) => isBlackKey(pc)).reduce((a, b) => a + b, 0);
    const total = usage.reduce((a, b) => a + b, 0);
    const fill = (pc: number) => (usage[pc] ? `rgba(255, 15, 0, ${0.25 + 0.75 * usage[pc] / max})` : undefined);

    return (
        <figure className="TpKeyboard">
            <svg viewBox="0 0 140 64" role="img" aria-label={`${title}: чёрных ${total ? percent(black / total) : '0%'}`}>
                {WHITE_PCS.map((pc, i) => (
                    <rect key={pc} x={i * 20 + 0.5} y={0.5} width={19} height={63} rx={2}
                          className="TpKey TpKey--white" style={{fill: fill(pc)}}/>
                ))}
                {BLACK_KEYS.map(([pc, slot]) => (
                    <rect key={pc} x={slot * 20 - 6} y={0.5} width={12} height={38} rx={1.5}
                          className="TpKey TpKey--black" style={{fill: fill(pc)}}/>
                ))}
            </svg>
            <figcaption>{title} · чёрных {total ? percent(black / total) : '0%'}</figcaption>
        </figure>
    );
}


const SPEEDS = [0.5, 0.75, 1, 1.25];

interface Playback {
    playing: boolean;
    // Share of piano samples loaded while the first play waits for them
    loading: number | null;
    position: number;
    speed: number;
    total: number;
    togglePlay: () => void;
    stop: () => void;
    seek: (seconds: number) => void;
    changeSpeed: (speed: number) => void;
}

// Owns a ScorePlayer for the timeline; onPosition gets score quarters on each frame and on seek.
// Handlers always go through playerRef, so a player replaced on a key change is never left running.
function usePlayback(timeline: Timeline, onPosition: (quarters: number) => void, onReset: () => void): Playback {
    const playerRef = useRef<ScorePlayer | null>(null);
    const callbacks = useRef({onPosition, onReset});
    callbacks.current = {onPosition, onReset};
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState<number | null>(null);
    const [position, setPosition] = useState(0);
    const [total, setTotal] = useState(0);
    const [speed, setSpeed] = useState(1);

    useEffect(() => {
        const player = new ScorePlayer(timeline, {
            onPosition: quarters => {
                setPosition(player.secondsOf(quarters));
                callbacks.current.onPosition(quarters);
            },
            onEnd: () => {
                setPlaying(false);
                setPosition(0);
                callbacks.current.onReset();
            },
            onLoading: setLoading,
        });
        playerRef.current = player;
        setPlaying(false);
        setLoading(null);
        setPosition(0);
        setTotal(player.totalSeconds);
        return () => {
            player.stop();
            if (playerRef.current === player) playerRef.current = null;
        };
    }, [timeline]);

    async function start(player: ScorePlayer, from: number, nextSpeed: number) {
        setPlaying(true);
        if (!(await player.play(from, nextSpeed)) || playerRef.current !== player) return;
        setLoading(null);
    }

    return {
        playing,
        loading,
        position,
        speed,
        total,
        togglePlay() {
            const player = playerRef.current;
            if (!player) return;
            if (playing) {
                player.stop();
                setPosition(player.position());
                setPlaying(false);
                setLoading(null);
                return;
            }
            const from = position >= player.totalSeconds ? 0 : position;
            callbacks.current.onPosition(player.quartersOf(from));
            start(player, from, speed);
        },
        stop() {
            playerRef.current?.stop();
            setPlaying(false);
            setLoading(null);
            setPosition(0);
            callbacks.current.onReset();
        },
        seek(seconds) {
            const player = playerRef.current;
            if (!player) return;
            setPosition(seconds);
            callbacks.current.onPosition(player.quartersOf(seconds));
            if (playing) start(player, seconds, speed);
        },
        changeSpeed(next) {
            setSpeed(next);
            const player = playerRef.current;
            if (player && playing) start(player, player.position(), next);
        },
    };
}

function PlayerBar({playback, disabled}: { playback: Playback; disabled: boolean }) {
    const {playing, loading, position, total, speed} = playback;
    return (
        <div className="TpPlayer TpNoPrint">
            <button className="TpPlay" onClick={playback.togglePlay} disabled={disabled}
                    aria-label={playing ? 'Пауза' : 'Слушать'} title={playing ? 'Пауза' : 'Слушать'}>
                {playing ? <Pause size={18}/> : <Play size={18}/>}
            </button>
            <button className="TpPlay TpPlay--ghost" onClick={playback.stop} aria-label="Стоп" title="Стоп">
                <Square size={14}/>
            </button>
            <input type="range" className="TpSeek" min={0} max={total || 1} step={0.1} value={position}
                   onChange={e => playback.seek(Number(e.target.value))} aria-label="Позиция"/>
            <span className="TpTime">
                {loading !== null
                    ? `Загружаю рояль… ${Math.round(loading * 100)}%`
                    : `${formatTime(position)} / ${formatTime(total)}`}
            </span>
            <select value={speed} onChange={e => playback.changeSpeed(Number(e.target.value))} aria-label="Темп">
                {SPEEDS.map(s => <option key={s} value={s}>{s === 1 ? 'темп 100%' : `${s * 100}%`}</option>)}
            </select>
        </div>
    );
}

// Index of the measure that contains the position (quarters), from sorted measure starts
function measureAt(starts: number[], quarters: number): number {
    let lo = 0;
    let hi = starts.length - 1;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (starts[mid] <= quarters + 1e-6) lo = mid;
        else hi = mid - 1;
    }
    return lo;
}

// Pages engraved by MuseScore; the playing measure is highlighted and a click on a measure jumps there.
// MIDI sources play their own timeline (real tempo, velocities, pedal) over the converted score.
function EngravedSheet({xml, sound, pdfRef}: {
    xml: string;
    sound: Timeline | null;
    pdfRef: MutableRefObject<(() => Promise<Uint8Array>) | null>;
}) {
    const [pages, setPages] = useState<string[]>([]);
    const [positions, setPositions] = useState<Engraving['positions']>(null);
    const [state, setState] = useState<'working' | 'ready' | 'error'>('working');
    const [measure, setMeasure] = useState<number | null>(null);

    const scoreTimeline = useMemo(() => buildTimeline(parseScore(xml)), [xml]);
    const timeline = sound ?? scoreTimeline;
    const starts = useMemo(() => scoreTimeline.measures ?? [0], [scoreTimeline]);
    const tempo = useMemo(() => new TempoMap(timeline.tempos), [timeline]);

    useEffect(() => {
        let cancelled = false;
        let engraving: Engraving | null = null;
        let urls: string[] = [];
        setState('working');
        engrave(xml).then(result => {
            if (cancelled) {
                result.dispose();
                return;
            }
            engraving = result;
            urls = result.pages.map(svg => URL.createObjectURL(new Blob([svg], {type: 'image/svg+xml'})));
            setPages(urls);
            setPositions(result.positions);
            pdfRef.current = result.pdf;
            setState('ready');
        }).catch(e => {
            console.error(e);
            if (!cancelled) setState('error');
        });
        return () => {
            cancelled = true;
            // The old pages stay on screen until the next engraving replaces them, so their URLs live on a bit
            const old = urls;
            window.setTimeout(() => old.forEach(u => URL.revokeObjectURL(u)), 30000);
            if (engraving && pdfRef.current === engraving.pdf) pdfRef.current = null;
            engraving?.dispose();
        };
    }, [xml, pdfRef]);

    const playback = usePlayback(timeline,
        quarters => setMeasure(measureAt(starts, quarters)),
        () => setMeasure(null));

    const box = measure !== null && positions?.elements[measure];
    const size = positions?.pageSize;

    function seekToClick(page: number, e: React.MouseEvent<HTMLDivElement>) {
        if (!positions || !size) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * size.width;
        const y = ((e.clientY - rect.top) / rect.height) * size.height;
        const hit = positions.elements.findIndex(m =>
            m.page === page && x >= m.x && x <= m.x + m.sx && y >= m.y && y <= m.y + m.sy);
        if (hit < 0 || hit >= starts.length) return;
        setMeasure(hit);
        playback.seek(tempo.toSeconds(starts[hit]));
    }

    return (
        <div className="TpSheet">
            <PlayerBar playback={playback} disabled={!timeline.notes.length}/>
            {state === 'error' && <p className="TpError">Не удалось нарисовать ноты.</p>}
            {state === 'working' && (
                <p className="TpWorking TpMuted"><LoaderCircle size={16}/> {pages.length ? 'Пересчитываю ноты…' : 'Рисую ноты…'}</p>
            )}
            <div className={`TpPages${state === 'working' ? ' is-stale' : ''}`}>
                {pages.map((url, i) => (
                    <div key={url} className="TpPageSheet" onClick={e => seekToClick(i, e)}>
                        <img src={url} alt={`Страница ${i + 1}`}/>
                        {box && size && box.page === i && (
                            <div className="TpMeasureMark" style={{
                                left: `${(box.x / size.width) * 100}%`,
                                top: `${(box.y / size.height) * 100}%`,
                                width: `${(box.sx / size.width) * 100}%`,
                                height: `${(box.sy / size.height) * 100}%`,
                            }}/>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function SheetView({xml}: { xml: string }) {
    const hostRef = useRef<HTMLDivElement>(null);
    const osmdRef = useRef<OpenSheetMusicDisplay | null>(null);
    // Score position of every cursor step, in quarter notes
    const stampsRef = useRef<number[]>([]);
    const cursorIndexRef = useRef(0);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

    const timeline = useMemo(() => buildTimeline(parseScore(xml)), [xml]);

    function resetCursor() {
        const cursor = osmdRef.current?.cursor;
        if (!cursor || !stampsRef.current.length) return;
        cursor.reset();
        cursor.hide();
        cursorIndexRef.current = 0;
    }

    function moveCursor(quarters: number) {
        const cursor = osmdRef.current?.cursor;
        const stamps = stampsRef.current;
        if (!cursor || !stamps.length) return;
        if (cursor.hidden) cursor.show();
        let i = cursorIndexRef.current;
        if (stamps[i] > quarters + 1e-6) {
            cursor.reset();
            i = 0;
        }
        // Stop at a jump back so a repeat in the engraving does not run ahead
        while (i + 1 < stamps.length && stamps[i + 1] <= quarters + 1e-6 && stamps[i + 1] >= stamps[i]) {
            cursor.next();
            i++;
        }
        cursorIndexRef.current = i;
    }

    const playback = usePlayback(timeline, moveCursor, resetCursor);

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        stampsRef.current = [];
        (async () => {
            try {
                const {OpenSheetMusicDisplay} = await import('opensheetmusicdisplay');
                if (cancelled || !hostRef.current) return;
                osmdRef.current ??= new OpenSheetMusicDisplay(hostRef.current, {
                    autoResize: false,
                    autoBeam: true,
                    backend: 'svg',
                    drawTitle: true,
                    drawPartNames: false,
                    // The page is not scrolled during playback; the cursor only marks the place
                    followCursor: false,
                });
                const osmd = osmdRef.current;
                await osmd.load(xml);
                if (cancelled) return;
                osmd.render();

                const stamps: number[] = [];
                const it = osmd.Sheet.MusicPartManager.getIterator();
                while (!it.EndReached) {
                    stamps.push(it.CurrentSourceTimestamp.RealValue * 4);
                    it.moveToNextVisibleVoiceEntry(false);
                }
                stampsRef.current = stamps;
                resetCursor();
                setStatus('ready');
            } catch (e) {
                console.error(e);
                if (!cancelled) setStatus('error');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [xml]);

    // OSMD's own autoResize leaves a window listener behind, so width changes are watched here
    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        let width = host.clientWidth;
        let timer: number | undefined;
        const observer = new ResizeObserver(() => {
            if (host.clientWidth === width) return;
            width = host.clientWidth;
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                const osmd = osmdRef.current;
                if (!osmd?.IsReadyToRender()) return;
                osmd.render();
                if (!osmd.cursor?.hidden) osmd.cursor?.update();
            }, 200);
        });
        observer.observe(host);
        return () => {
            observer.disconnect();
            window.clearTimeout(timer);
        };
    }, []);

    // Printing switches to A4 pages so systems are not cut in half, then back to one long page
    useEffect(() => {
        const layout = (pageFormat: string) => () => {
            const osmd = osmdRef.current;
            if (!osmd?.IsReadyToRender()) return;
            osmd.setOptions({pageFormat});
            osmd.render();
            if (!osmd.cursor?.hidden) osmd.cursor?.update();
        };
        const toPages = layout('A4_P');
        const toScreen = layout('Endless');
        window.addEventListener('beforeprint', toPages);
        window.addEventListener('afterprint', toScreen);
        return () => {
            window.removeEventListener('beforeprint', toPages);
            window.removeEventListener('afterprint', toScreen);
        };
    }, []);

    return (
        <div className="TpSheet">
            <PlayerBar playback={playback} disabled={!timeline.notes.length}/>
            {status === 'loading' && <p className="TpMuted TpNoPrint">Рисую ноты…</p>}
            {status === 'error' &&
                <p className="TpError TpNoPrint">Не удалось нарисовать ноты, но слушать и скачивать можно.</p>}
            <div ref={hostRef} className="TpSheetHost"/>
        </div>
    );
}

const ROW_HEIGHT = 7;
const KEYS_WIDTH = 34;
const MAX_CANVAS_WIDTH = 30000;

// Piano-roll for MIDI: time runs right, pitch runs up; black-key notes are drawn dark
function MidiView({timeline}: { timeline: Timeline }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const rollRef = useRef<HTMLCanvasElement>(null);
    const keysRef = useRef<HTMLCanvasElement>(null);
    const [playhead, setPlayhead] = useState<number | null>(null);

    const tempo = useMemo(() => new TempoMap(timeline.tempos), [timeline]);
    const layout = useMemo(() => {
        const pitches = timeline.notes.map(n => n.midi);
        const low = Math.max(0, (pitches.length ? Math.min(...pitches) : 60) - 2);
        const high = Math.min(127, (pitches.length ? Math.max(...pitches) : 72) + 2);
        const length = Math.max(timeline.length, 1);
        const perQuarter = Math.max(4, Math.min(40, MAX_CANVAS_WIDTH / length));
        return {
            low, high, perQuarter,
            width: Math.ceil(length * perQuarter) + 20,
            height: (high - low + 1) * ROW_HEIGHT,
        };
    }, [timeline]);

    const playback = usePlayback(timeline, quarters => {
        setPlayhead(quarters);
        // Keep the playhead in view while playing
        const scroller = scrollRef.current;
        if (!scroller) return;
        const x = quarters * layout.perQuarter;
        if (x < scroller.scrollLeft || x > scroller.scrollLeft + scroller.clientWidth * 0.8) {
            scroller.scrollLeft = x - scroller.clientWidth * 0.2;
        }
    }, () => setPlayhead(null));

    useEffect(() => {
        const {low, high, perQuarter, width, height} = layout;
        const roll = rollRef.current;
        const keys = keysRef.current;
        if (!roll || !keys) return;
        const ratio = Math.min(window.devicePixelRatio || 1, MAX_CANVAS_WIDTH / width);
        const rowY = (midi: number) => (high - midi) * ROW_HEIGHT;

        roll.width = Math.floor(width * ratio);
        roll.height = Math.floor(height * ratio);
        roll.style.width = `${width}px`;
        roll.style.height = `${height}px`;
        const ctx = roll.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        for (let m = low; m <= high; m++) {
            if (isBlackKey(m)) {
                ctx.fillStyle = '#f1f1f4';
                ctx.fillRect(0, rowY(m), width, ROW_HEIGHT);
            }
            if (m % 12 === 0) {
                ctx.fillStyle = '#d8d8de';
                ctx.fillRect(0, rowY(m) + ROW_HEIGHT - 1, width, 1);
            }
        }
        if (perQuarter >= 8) {
            ctx.fillStyle = '#ececf0';
            for (let q = 0; q <= timeline.length; q++) ctx.fillRect(q * perQuarter, 0, 1, height);
        }
        for (const n of timeline.notes) {
            ctx.fillStyle = isBlackKey(n.midi) ? '#2a2a30' : '#FF0F00';
            ctx.fillRect(n.start * perQuarter, rowY(n.midi) + 0.5,
                Math.max(2, (n.end - n.start) * perQuarter - 1), ROW_HEIGHT - 1);
        }

        keys.width = Math.floor(KEYS_WIDTH * ratio);
        keys.height = Math.floor(height * ratio);
        keys.style.width = `${KEYS_WIDTH}px`;
        keys.style.height = `${height}px`;
        const k = keys.getContext('2d');
        if (!k) return;
        k.setTransform(ratio, 0, 0, ratio, 0, 0);
        k.fillStyle = '#ffffff';
        k.fillRect(0, 0, KEYS_WIDTH, height);
        k.font = '6px sans-serif';
        k.textBaseline = 'middle';
        for (let m = low; m <= high; m++) {
            if (isBlackKey(m)) {
                k.fillStyle = '#2a2a30';
                k.fillRect(0, rowY(m), KEYS_WIDTH * 0.62, ROW_HEIGHT);
            } else if (m % 12 === 0) {
                k.fillStyle = '#6b6b76';
                k.fillText(`C${m / 12 - 1}`, KEYS_WIDTH * 0.66, rowY(m) + ROW_HEIGHT / 2);
            }
            k.fillStyle = '#d8d8de';
            k.fillRect(0, rowY(m) + ROW_HEIGHT - 0.5, KEYS_WIDTH, 0.5);
        }
    }, [layout, timeline]);

    return (
        <div className="TpSheet">
            <PlayerBar playback={playback} disabled={!timeline.notes.length}/>
            <div className="TpLegend TpMuted">
                <span><i className="TpSwatch TpSwatch--white"/> на белых клавишах</span>
                <span><i className="TpSwatch TpSwatch--black"/> на чёрных клавишах</span>
                <span>нажмите на дорожку, чтобы перейти к месту</span>
            </div>
            <div className="TpRoll">
                <canvas ref={keysRef} className="TpRollKeys" aria-hidden="true"/>
                <div ref={scrollRef} className="TpRollScroll">
                    <div className="TpRollInner" style={{width: layout.width}}>
                        <canvas ref={rollRef} role="img" aria-label="Piano-roll"
                                onClick={e => playback.seek(tempo.toSeconds(e.nativeEvent.offsetX / layout.perQuarter))}/>
                        {playhead !== null && (
                            <div className="TpPlayhead" style={{left: playhead * layout.perQuarter}}/>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
